"use client"

import { useState, useEffect } from "react"
import { PencilIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import VideoModal from "../components/vedioModal.js"
import Sidebar from "../components/Sidebar.js"
import { fetchVideos, deleteVideo } from "../comman/LearningVedio.js"

const LearningVideoTable = () => {
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [videoToEdit, setVideoToEdit] = useState(null)

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const data = await fetchVideos()
        setVideos(data)
        setError(null)
      } catch (err) {
        setError("Failed to load videos. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    loadVideos()
  }, [])

  const handleDelete = async (videoId) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      try {
        await deleteVideo(videoId)
        setVideos((prevVideos) => prevVideos.filter((video) => video._id !== videoId))
      } catch (error) {
        console.error("Failed to delete video:", error)
        setError("Failed to delete the video. Please try again.")
      }
    }
  }

  const handleEdit = (video) => {
    setVideoToEdit(video)
    setIsModalOpen(true)
  }

  const handleAddVideo = async (newVideoData) => {
    setVideos((prevVideos) => [...prevVideos, { ...newVideoData, _id: Date.now() }])
    setIsModalOpen(false)
  }

  const handleUpdateVideo = async (updatedVideo) => {
    setVideos((prevVideos) => prevVideos.map((video) => (video._id === updatedVideo._id ? updatedVideo : video)))
    setIsModalOpen(false)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setVideoToEdit(null) // Reset videoToEdit
  }

  const filteredVideos = videos.filter(
    (video) =>
      video.videoTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.videoCategory?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.videoLink?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64 w-full max-w-[100vw] overflow-hidden">
        <div className="max-w-full overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Learning Videos</h1>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {error}</span>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 overflow-hidden">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                  placeholder="Search videos"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <MagnifyingGlassIcon className="w-5 h-5 text-gray-500" />
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Upload Video
              </button>
            </div>
            <div className="overflow-x-auto -mx-4 md:-mx-6">
              <div className="inline-block min-w-full align-middle p-4 md:p-6">
                <div className="overflow-hidden border rounded-lg">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-[#12143F]">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Sr. No
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Video Title
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Category
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Video Link
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredVideos.map((video, index) => (
                          <tr key={video._id} className="hover:bg-gray-50 transition duration-150">
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{index + 1}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">
                              {video.videoTitle}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 max-w-[150px] truncate">
                              {video.videoCategory}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900">
                              <a
                                href={video.videoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700 transition-colors duration-200"
                              >
                                View Video
                              </a>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-center space-x-3">
                                <button
                                  onClick={() => handleEdit(video)}
                                  className="text-blue-500 hover:text-blue-700 transition-colors duration-200"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(video._id)}
                                  className="text-red-500 hover:text-red-700 transition-colors duration-200"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <VideoModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          videoToEdit={videoToEdit}
          onAddVideo={handleAddVideo}
          onUpdateVideo={handleUpdateVideo}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}

export default LearningVideoTable